'use client';

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

const PWA_INSTALL_DISMISS_STORAGE_KEY = 'cleanor-pwa-install-dismissed-at';
const PWA_INSTALL_DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 3;
const PWA_UPDATE_DISMISS_STORAGE_KEY = 'cleanor-pwa-update-dismissed-build';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

type PwaInstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

type PwaInstallContextValue = {
  applyUpdate: () => Promise<void>;
  canInstall: boolean;
  canInstallManually: boolean;
  dismissPrompt: () => void;
  dismissUpdate: () => void;
  install: () => Promise<PwaInstallOutcome>;
  installHelpText: string;
  isInstalled: boolean;
  isInstallSupported: boolean;
  isPromptVisible: boolean;
  isUpdating: boolean;
  updateAvailable: boolean;
};

const PwaInstallContext = createContext<PwaInstallContextValue | null>(null);

function isInstalledStandalone() {
  const iosNavigator = window.navigator as Navigator & { standalone?: boolean };

  return (
    window.matchMedia('(display-mode: standalone)').matches || iosNavigator.standalone === true
  );
}

function isInstallDismissed() {
  try {
    const rawValue = window.localStorage.getItem(PWA_INSTALL_DISMISS_STORAGE_KEY);

    if (!rawValue) {
      return false;
    }

    const dismissedAt = Number(rawValue);

    if (!Number.isFinite(dismissedAt)) {
      return false;
    }

    return Date.now() - dismissedAt < PWA_INSTALL_DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function saveDismissedInstallPrompt() {
  try {
    window.localStorage.setItem(PWA_INSTALL_DISMISS_STORAGE_KEY, String(Date.now()));
  } catch {
    // Ignore storage failures and keep the prompt hidden only for this runtime.
  }
}

function clearDismissedInstallPrompt() {
  try {
    window.localStorage.removeItem(PWA_INSTALL_DISMISS_STORAGE_KEY);
  } catch {
    // Ignore storage failures. The app can still continue.
  }
}

function getDismissedUpdateBuildId() {
  try {
    return window.localStorage.getItem(PWA_UPDATE_DISMISS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function saveDismissedUpdateBuildId(buildId: string) {
  try {
    window.localStorage.setItem(PWA_UPDATE_DISMISS_STORAGE_KEY, buildId);
  } catch {
    // Ignore storage failures and keep the prompt hidden only for this runtime.
  }
}

function clearDismissedUpdateBuildId() {
  try {
    window.localStorage.removeItem(PWA_UPDATE_DISMISS_STORAGE_KEY);
  } catch {
    // Ignore storage failures. The app can still continue.
  }
}

function getInstallHelpText(canInstall: boolean) {
  if (canInstall) {
    return 'Install Cleanor for faster repeat launches, app-style navigation, and one-tap access on this device.';
  }

  const userAgent = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|Edg|OPR/.test(userAgent);

  if (isIos && isSafari) {
    return 'On iPhone or iPad, open the Share menu in Safari and choose Add to Home Screen.';
  }

  return 'If your browser supports install, the browser menu may show Install app. Once the site becomes installable, Cleanor also surfaces a direct install button here.';
}

function supportsManualInstall() {
  const userAgent = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|Edg|OPR/.test(userAgent);

  return isIos && isSafari;
}

export default function PwaInstallProvider({ children }: { children: ReactNode }) {
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const promptTimerRef = useRef<number | null>(null);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const waitingWorkerRef = useRef<ServiceWorker | null>(null);
  const initialBuildIdRef = useRef<string | null>(null);
  const nextBuildIdRef = useRef<string | null>(null);
  const shouldReloadOnControllerChangeRef = useRef(false);
  const [canInstall, setCanInstall] = useState(false);
  const [canInstallManually, setCanInstallManually] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallSupported, setIsInstallSupported] = useState(false);
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [installHelpText, setInstallHelpText] = useState(
    'Install Cleanor for faster repeat launches and one-tap access on this device.',
  );
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    let updateIntervalId: number | null = null;

    function syncInstalledState() {
      const installed = isInstalledStandalone();
      setIsInstalled(installed);

      if (installed) {
        setCanInstall(false);
        setIsPromptVisible(false);
        deferredPromptRef.current = null;
        clearDismissedInstallPrompt();
      }
    }

    function setWaitingWorker(worker: ServiceWorker | null, nextBuildId?: string | null) {
      waitingWorkerRef.current = worker;

      if (worker) {
        if (nextBuildId) {
          nextBuildIdRef.current = nextBuildId;
        }

        const dismissedBuildId = getDismissedUpdateBuildId();
        if (!nextBuildIdRef.current || dismissedBuildId !== nextBuildIdRef.current) {
          setUpdateAvailable(true);
        }
      } else {
        setUpdateAvailable(false);
        nextBuildIdRef.current = null;
      }
    }

    async function fetchBuildId() {
      try {
        const response = await fetch('/BUILD_ID', {
          cache: 'no-store',
          headers: { 'cache-control': 'no-store' },
        });

        if (!response.ok) {
          return null;
        }

        const buildId = (await response.text()).trim();
        return buildId || null;
      } catch {
        return null;
      }
    }

    async function checkForAppUpdate(reason: 'initial' | 'focus' | 'interval') {
      const latestBuildId = await fetchBuildId();

      if (!latestBuildId) {
        return;
      }

      if (!initialBuildIdRef.current) {
        initialBuildIdRef.current = latestBuildId;
        return;
      }

      if (latestBuildId === initialBuildIdRef.current) {
        if (!waitingWorkerRef.current) {
          setUpdateAvailable(false);
          nextBuildIdRef.current = null;
        }

        return;
      }

      nextBuildIdRef.current = latestBuildId;
      if (getDismissedUpdateBuildId() !== latestBuildId) {
        setUpdateAvailable(true);
      }

      if (reason !== 'initial') {
        void registrationRef.current?.update();
      }
    }

    function watchRegistration(registration: ServiceWorkerRegistration) {
      registrationRef.current = registration;

      if (registration.waiting) {
        setWaitingWorker(registration.waiting);
      }

      registration.addEventListener('updatefound', () => {
        const installingWorker = registration.installing;

        if (!installingWorker) {
          return;
        }

        installingWorker.addEventListener('statechange', () => {
          if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setWaitingWorker(registration.waiting ?? installingWorker);
          }
        });
      });
    }

    async function registerServiceWorker() {
      if (!('serviceWorker' in navigator)) {
        return;
      }

      try {
        const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        watchRegistration(registration);
      } catch {
        // Ignore registration failures so the rest of the site keeps working.
      }
    }

    function showInstallPromptIfAllowed() {
      if (isInstallDismissed() || isInstalledStandalone()) {
        return;
      }

      if (promptTimerRef.current !== null) {
        window.clearTimeout(promptTimerRef.current);
      }

      promptTimerRef.current = window.setTimeout(() => {
        setIsPromptVisible(true);
      }, 1200);
    }

    function handleBeforeInstallPrompt(event: Event) {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      deferredPromptRef.current = installEvent;
      setCanInstall(true);
      setInstallHelpText(getInstallHelpText(true));
      showInstallPromptIfAllowed();
    }

    function handleAppInstalled() {
      syncInstalledState();
    }

    function handleControllerChange() {
      if (!shouldReloadOnControllerChangeRef.current) {
        return;
      }

      shouldReloadOnControllerChangeRef.current = false;
      window.location.reload();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        void checkForAppUpdate('focus');
      }
    }

    // Self-heal after a deploy: if a lazily-loaded chunk fails to load (a stale
    // cached app shell references a chunk the new build no longer serves), do a
    // single hard reload to fetch a consistent build instead of leaving the user
    // stuck on the "Something went wrong" error boundary. Guarded so it can never
    // loop.
    function handleChunkLoadError(event: Event | PromiseRejectionEvent) {
      const reason = (event as PromiseRejectionEvent).reason ?? (event as ErrorEvent).error ?? null;
      const message = `${reason?.message ?? reason ?? (event as ErrorEvent).message ?? ''}`;
      const name = `${reason?.name ?? ''}`;
      const isChunkError =
        name === 'ChunkLoadError' ||
        /Loading chunk [\w./-]+ failed/i.test(message) ||
        /Loading CSS chunk/i.test(message) ||
        /(Failed to fetch|error loading) dynamically imported module/i.test(message) ||
        /importing a module script failed/i.test(message);

      if (!isChunkError) {
        return;
      }

      try {
        const key = 'cleanor-chunk-reload-at';
        const last = Number(window.sessionStorage.getItem(key) || '0');
        if (Date.now() - last < 30000) {
          return;
        }
        window.sessionStorage.setItem(key, String(Date.now()));
      } catch {
        // If sessionStorage is unavailable, still attempt one reload.
      }

      window.location.reload();
    }

    syncInstalledState();
    setCanInstallManually(supportsManualInstall());
    setIsInstallSupported(
      window.isSecureContext && ('serviceWorker' in navigator || 'onappinstalled' in window),
    );
    setInstallHelpText(getInstallHelpText(false));
    void registerServiceWorker();
    void checkForAppUpdate('initial');

    updateIntervalId = window.setInterval(
      () => {
        void checkForAppUpdate('interval');
      },
      1000 * 60 * 15,
    );

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);
    navigator.serviceWorker?.addEventListener('controllerchange', handleControllerChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('error', handleChunkLoadError);
    window.addEventListener('unhandledrejection', handleChunkLoadError);
    mediaQuery.addEventListener('change', syncInstalledState);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', handleAppInstalled);
      navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('error', handleChunkLoadError);
      window.removeEventListener('unhandledrejection', handleChunkLoadError);
      mediaQuery.removeEventListener('change', syncInstalledState);

      if (promptTimerRef.current !== null) {
        window.clearTimeout(promptTimerRef.current);
      }

      if (updateIntervalId !== null) {
        window.clearInterval(updateIntervalId);
      }
    };
  }, []);

  function dismissPrompt() {
    saveDismissedInstallPrompt();
    setIsPromptVisible(false);
  }

  async function install(): Promise<PwaInstallOutcome> {
    const deferredPrompt = deferredPromptRef.current;

    if (!deferredPrompt) {
      return 'unavailable';
    }

    setIsPromptVisible(false);
    await deferredPrompt.prompt();
    const choiceResult = await deferredPrompt.userChoice;

    deferredPromptRef.current = null;
    setCanInstall(false);

    if (choiceResult.outcome === 'dismissed') {
      saveDismissedInstallPrompt();
      return 'dismissed';
    }

    clearDismissedInstallPrompt();
    return 'accepted';
  }

  function dismissUpdate() {
    if (nextBuildIdRef.current) {
      saveDismissedUpdateBuildId(nextBuildIdRef.current);
    }

    setUpdateAvailable(false);
  }

  async function applyUpdate() {
    setIsUpdating(true);
    clearDismissedUpdateBuildId();

    const waitingWorker = waitingWorkerRef.current;

    if (waitingWorker) {
      shouldReloadOnControllerChangeRef.current = true;
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      return;
    }

    window.location.reload();
  }

  const value = useMemo<PwaInstallContextValue>(
    () => ({
      applyUpdate,
      canInstall,
      canInstallManually,
      dismissPrompt,
      dismissUpdate,
      install,
      installHelpText,
      isInstalled,
      isInstallSupported,
      isPromptVisible,
      isUpdating,
      updateAvailable,
    }),
    [
      canInstall,
      canInstallManually,
      installHelpText,
      isInstalled,
      isInstallSupported,
      isPromptVisible,
      isUpdating,
      updateAvailable,
    ],
  );

  return <PwaInstallContext.Provider value={value}>{children}</PwaInstallContext.Provider>;
}

export function usePwaInstall() {
  const context = useContext(PwaInstallContext);

  if (!context) {
    throw new Error('usePwaInstall must be used inside PwaInstallProvider.');
  }

  return context;
}
